import React from 'react';
import { 
    BarChart3, FileText, TrendingUp, Shield, 
    Clock, Users, Database, CheckCircle 
} from 'lucide-react';
import Footer from './Footer';
import Header from './Header';

const Services = () => {
    const services = [
        {
            icon: BarChart3,
            title: 'Financial Analytics',
            description: 'Comprehensive dashboards and reports to track your business performance in real-time.',
            features: ['Real-time tracking', 'Custom reports', 'Data visualization', 'Export capabilities']
        },
        {
            icon: FileText,
            title: 'Cash Flow Management',
            description: 'Monitor and optimize your cash flow with predictive insights and automated tracking.',
            features: ['Cash flow forecasting', 'Automated reconciliation', 'Payment tracking', 'Budget planning']
        },
        {
            icon: TrendingUp,
            title: 'Revenue Optimization',
            description: 'Identify revenue opportunities and track performance metrics that matter.',
            features: ['Revenue analytics', 'Growth trends', 'Profit margins', 'KPI tracking']
        },
        {
            icon: Database,
            title: 'Data Integration',
            description: 'Seamlessly integrate with your existing tools and accounting software.',
            features: ['API access', 'Multi-platform sync', 'Automated imports', 'Cloud storage']
        },
        {
            icon: Shield,
            title: 'Security & Compliance',
            description: 'Bank-grade security with complete compliance to financial regulations.',
            features: ['Data encryption', 'GDPR compliant', 'Regular backups', 'Audit trails']
        },
        {
            icon: Users,
            title: 'Team Collaboration',
            description: 'Work together with your team with role-based access and permissions.',
            features: ['User management', 'Access controls', 'Activity logs', 'Team sharing']
        }
    ];

    return (
        <>
        <Header/>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <h1 className="text-5xl font-bold mb-4">Our Services</h1>
                    <p className="text-xl text-slate-200 max-w-1xl">
                        Comprehensive financial management solutions tailored to your business needs
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {services.map((service, idx) => {
                        const Icon = service.icon;
                        return (
                            <div key={idx} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow">
                                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                                    <Icon className="w-7 h-7 text-blue-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-800 mb-4">{service.title}</h3>
                                <p className="text-slate-600 mb-6 leading-relaxed">
                                    {service.description}
                                </p>
                                <ul className="space-y-2">
                                    {service.features.map((feature, i) => (
                                        <li key={i} className="flex items-center text-sm text-slate-600">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
                    <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Why Choose?</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Clock className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">Save Time</h3>
                            <p className="text-slate-600 text-sm">
                                Automate repetitive tasks and get insights in minutes, not hours
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">Grow Faster</h3>
                            <p className="text-slate-600 text-sm">
                                Make data-driven decisions that accelerate your business growth
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8 text-purple-600" />
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">Stay Secure</h3>
                            <p className="text-slate-600 text-sm">
                                Enterprise-grade security keeps your financial data protected
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Footer/>
        </>
    );
};

export default Services;